import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getPublicSolutions, type SolutionDto } from '@/api/solution';
import { fetchDictBatch } from '@/api/dict';
import { usePublicSubPageContent } from '@/hooks/usePublicSubPageContent';

type SolutionView = SolutionDto & { sortOrder?: number };

function readText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function parseSaving(val?: string | null): number {
  const raw = readText(val ?? '');
  if (!raw) return 0;
  return parseInt(raw.replace('%', ''), 10) || 0;
}

function clampStars(value: unknown): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(5, Math.round(value)));
}

function SolutionListHeader() {
  const { content: subPageContent, error } = usePublicSubPageContent();
  const content = subPageContent.solutionList;

  return (
    <div className="bg-gradient-to-br from-[#0f172a] to-[#1e3a5f] pt-28 pb-16 px-6">
      <div className="max-w-7xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white/70 text-sm mb-4">
          <i className="ri-lightbulb-line"></i>
          {content.badge || '无'}
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{content.title || '无'}</h1>
        <p className="text-white/60 max-w-xl mx-auto">{content.subtitle || '无'}</p>
        {error && <p className="text-xs text-red-300 mt-3">页头内容加载失败：{error}</p>}
      </div>
    </div>
  );
}

export default function SolutionsPage() {
  const [solutions, setSolutions] = useState<SolutionView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [dictCategories, setDictCategories] = useState<{ label: string; value: string }[]>([]);
  const [dictDifficulties, setDictDifficulties] = useState<{ label: string; value: string; color?: string | null }[]>([]);

  useEffect(() => {
    fetchDictBatch(['solution_category', 'solution_difficulty'])
      .then((dicts) => {
        if (dicts['solution_category']) {
          setDictCategories(dicts['solution_category'].map((x) => ({ label: x.label, value: x.value })));
        }
        if (dicts['solution_difficulty']) {
          setDictDifficulties(dicts['solution_difficulty'].map((x) => ({ label: x.label, value: x.value, color: x.color })));
        }
      })
      .catch((err) => console.error('Failed to load solution dictionaries in public catalog:', err));
  }, []);

  const getCategoryLabel = useCallback((cat: string) => {
    if (cat === '全部') return '全部';
    const found = dictCategories.find((c) => c.value === cat);
    return found ? found.label : cat;
  }, [dictCategories]);

  const getDifficultyItem = useCallback((difficulty: string) => {
    const raw = readText(difficulty);
    if (!raw) return null;
    return dictDifficulties.find((d) => d.value === raw || d.label === raw) || null;
  }, [dictDifficulties]);

  const getDifficultyLabel = useCallback((difficulty: string) => {
    const item = getDifficultyItem(difficulty);
    return item?.label || difficulty;
  }, [getDifficultyItem]);

  const refreshSolutions = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await getPublicSolutions();
      setSolutions(Array.isArray(res?.list) ? (res.list as SolutionView[]) : []);
    } catch (err) {
      setSolutions([]);
      setLoadError(err instanceof Error ? err.message : '获取方案列表失败');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshSolutions();
    const handleCustom = () => void refreshSolutions();
    window.addEventListener('wanxiang_solutions_updated', handleCustom);
    return () => {
      window.removeEventListener('wanxiang_solutions_updated', handleCustom);
    };
  }, [refreshSolutions]);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(
      solutions
        .map((s) => readText(s.category))
        .filter(Boolean),
    ));
    return ['全部', ...cats];
  }, [solutions]);

  const [activeCategory, setActiveCategory] = useState('全部');

  useEffect(() => {
    const cat = searchParams.get('category');
    if (cat) setActiveCategory(cat);
  }, [searchParams]);

  const allTags = useMemo(() => {
    const tags = solutions.flatMap((s) => (s.tags ?? []).map((tag) => readText(tag)).filter(Boolean));
    return Array.from(new Set(tags));
  }, [solutions]);

  const [search, setSearch] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState<string[]>([]);
  const [minStars, setMinStars] = useState(0);
  const [maxStars, setMaxStars] = useState(5);
  const [minSaving, setMinSaving] = useState(0);
  const [maxSaving, setMaxSaving] = useState(100);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const toggleDifficulty = (d: string) => {
    setSelectedDifficulties((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d],
    );
  };

  const resetAdvanced = () => {
    setSelectedTags([]);
    setSelectedDifficulties([]);
    setMinStars(0);
    setMaxStars(5);
    setMinSaving(0);
    setMaxSaving(100);
  };

  const hasAdvancedFilter =
    selectedTags.length > 0 ||
    selectedDifficulties.length > 0 ||
    minStars > 0 ||
    maxStars < 5 ||
    minSaving > 0 ||
    maxSaving < 100;

  const PAGE_SIZE = 6;
  const [currentPage, setCurrentPage] = useState(1);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sortedSolutions = useMemo(() => {
    return [...solutions].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return (a.sortOrder ?? 999) - (b.sortOrder ?? 999);
    });
  }, [solutions]);

  const filtered = useMemo(() => {
    const keyword = search.trim();
    return sortedSolutions.filter((s) => {
      const title = readText(s.title);
      const description = readText(s.description);
      const opensource = readText(s.opensource);
      const tags = (s.tags ?? []).map((tag) => readText(tag)).filter(Boolean);
      const difficulty = readText(s.difficulty);
      const category = readText(s.category);
      const stars = clampStars(s.stars);

      const matchCat = activeCategory === '全部' || category === activeCategory;
      const matchSearch =
        !keyword ||
        title.includes(keyword) ||
        description.includes(keyword) ||
        opensource.includes(keyword);
      const matchTags = selectedTags.length === 0 || selectedTags.every((t) => tags.includes(t));
      const matchDiff = selectedDifficulties.length === 0 || selectedDifficulties.includes(difficulty);
      const matchStars = stars >= minStars && stars <= maxStars;
      const saving = parseSaving(s.costSaving);
      const matchSaving = saving >= minSaving && saving <= maxSaving;
      return matchCat && matchSearch && matchTags && matchDiff && matchStars && matchSaving;
    });
  }, [sortedSolutions, activeCategory, search, selectedTags, selectedDifficulties, minStars, maxStars, minSaving, maxSaving]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, search, selectedTags, selectedDifficulties, minStars, maxStars, minSaving, maxSaving]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Navbar />
      <SolutionListHeader />

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder="搜索方案名称、开源软件..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0ea5e9] transition-colors"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-[#0ea5e9] text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-[#0ea5e9] hover:text-[#0ea5e9]'
                }`}
              >
                {getCategoryLabel(cat)}
              </button>
            ))}
            <button
              onClick={() => setShowAdvanced((v) => !v)}
              className={`px-4 py-2 rounded-xl text-sm font-medium border cursor-pointer whitespace-nowrap flex items-center gap-1.5 transition-all ${
                showAdvanced || hasAdvancedFilter
                  ? 'bg-[#0ea5e9]/10 border-[#0ea5e9] text-[#0ea5e9]'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-[#0ea5e9] hover:text-[#0ea5e9]'
              }`}
            >
              <i className="ri-equalizer-line"></i>
              高级筛选
              {hasAdvancedFilter && (
                <span className="w-4 h-4 flex items-center justify-center bg-[#0ea5e9] text-white text-xs rounded-full">
                  {selectedTags.length + selectedDifficulties.length + (minStars > 0 ? 1 : 0) + (maxStars < 5 ? 1 : 0) + (minSaving > 0 ? 1 : 0) + (maxSaving < 100 ? 1 : 0)}
                </span>
              )}
            </button>
          </div>
        </div>

        {showAdvanced && (
          <div className="bg-white border border-[#0ea5e9]/20 rounded-2xl p-5 mb-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                <i className="ri-equalizer-line text-[#0ea5e9]"></i>
                高级筛选
              </h3>
              {hasAdvancedFilter && (
                <button onClick={resetAdvanced} className="text-xs text-red-400 hover:text-red-500 cursor-pointer flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-refresh-line"></i>重置筛选
                </button>
              )}
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">部署难度（可多选）</p>
              <div className="flex flex-wrap gap-2">
                {dictDifficulties.map((d) => {
                  const active = selectedDifficulties.includes(d.value);
                  const color = d.color || '#64748b';
                  return (
                    <button
                      key={d.value}
                      onClick={() => toggleDifficulty(d.value)}
                      className={`px-4 py-1.5 rounded-full text-xs font-medium border-2 cursor-pointer whitespace-nowrap transition-all flex items-center gap-1 ${active ? 'text-white border-transparent' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                      style={active ? { backgroundColor: color } : {}}
                    >
                      {active && <i className="ri-check-line text-xs"></i>}
                      <span>{d.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">按标签筛选（可多选）</p>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border cursor-pointer whitespace-nowrap transition-all ${
                      selectedTags.includes(tag)
                        ? 'bg-[#0ea5e9] border-[#0ea5e9] text-white'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-[#0ea5e9] hover:text-[#0ea5e9]'
                    }`}
                  >
                    {selectedTags.includes(tag) && <i className="ri-check-line mr-1"></i>}
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">推荐指数范围</p>
                <div className="flex items-center gap-3">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button key={s} onClick={() => { setMinStars(s === minStars ? 0 : s); if (s > maxStars) setMaxStars(s); }} className="cursor-pointer">
                        <i className={`ri-star-fill text-lg ${s >= minStars && s <= maxStars && minStars > 0 ? 'text-[#f97316]' : 'text-gray-200'} hover:text-[#f97316] transition-colors`}></i>
                      </button>
                    ))}
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {minStars === 0 ? '不限' : `${minStars} ~ ${maxStars} 星`}
                  </span>
                </div>
                {minStars > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-gray-500">至多：</span>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <button key={s} onClick={() => { setMaxStars(s); if (s < minStars) setMinStars(s); }} className="cursor-pointer">
                          <i className={`ri-star-fill text-sm ${s <= maxStars ? 'text-[#f97316]' : 'text-gray-200'}`}></i>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-600 mb-3">成本节省比例</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-14 whitespace-nowrap">最低节省</span>
                    <input type="range" min={0} max={100} step={5} value={minSaving} onChange={(e) => { const v = Number(e.target.value); setMinSaving(v); if (v > maxSaving) setMaxSaving(v); }} className="flex-1 accent-[#0ea5e9]" />
                    <span className="text-xs font-semibold text-[#0ea5e9] w-10 text-right whitespace-nowrap">{minSaving}%</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-14 whitespace-nowrap">最高节省</span>
                    <input type="range" min={0} max={100} step={5} value={maxSaving} onChange={(e) => { const v = Number(e.target.value); setMaxSaving(v); if (v < minSaving) setMinSaving(v); }} className="flex-1 accent-[#0ea5e9]" />
                    <span className="text-xs font-semibold text-[#0ea5e9] w-10 text-right whitespace-nowrap">{maxSaving}%</span>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">当前范围：{minSaving}% ~ {maxSaving}%</p>
              </div>
            </div>

            {hasAdvancedFilter && (
              <div className="pt-3 border-t border-gray-100 flex flex-wrap gap-2">
                {selectedDifficulties.map((d) => {
                  const item = getDifficultyItem(d);
                  const color = item?.color || '#64748b';
                  return (
                    <span key={d} className="flex items-center gap-1 px-2.5 py-1 text-white text-xs rounded-full" style={{ backgroundColor: color }}>
                      {getDifficultyLabel(d)}
                      <button onClick={() => toggleDifficulty(d)} className="cursor-pointer ml-0.5">
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </span>
                  );
                })}
                {selectedTags.map((t) => (
                  <span key={t} className="flex items-center gap-1 px-2.5 py-1 bg-[#0ea5e9]/10 text-[#0ea5e9] text-xs rounded-full">
                    {t}
                    <button onClick={() => toggleTag(t)} className="cursor-pointer hover:text-[#0ea5e9] ml-0.5">
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </span>
                ))}
                {minStars > 0 && (
                  <span className="flex items-center gap-1 px-2.5 py-1 bg-[#f97316]/10 text-[#f97316] text-xs rounded-full">
                    {minStars}~{maxStars}星
                    <button onClick={() => { setMinStars(0); setMaxStars(5); }} className="cursor-pointer ml-0.5">
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </span>
                )}
                {(minSaving > 0 || maxSaving < 100) && (
                  <span className="flex items-center gap-1 px-2.5 py-1 bg-[#10b981]/10 text-[#10b981] text-xs rounded-full">
                    节省 {minSaving}~{maxSaving}%
                    <button onClick={() => { setMinSaving(0); setMaxSaving(100); }} className="cursor-pointer ml-0.5">
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-gray-500">
            共找到 <strong className="text-gray-900">{filtered.length}</strong> 个方案
            {hasAdvancedFilter && <span className="text-[#0ea5e9] ml-1">（已应用高级筛选）</span>}
          </p>
          {totalPages > 1 && (
            <p className="text-sm text-gray-400">第 {currentPage} / {totalPages} 页</p>
          )}
        </div>

        {loadError && (
          <div className="mb-6 bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm text-red-500 flex items-center justify-between">
            <span>方案列表加载失败：{loadError}</span>
            <button onClick={() => void refreshSolutions()} className="text-xs px-2.5 py-1 rounded-md bg-red-500 text-white hover:bg-red-600 transition-colors">
              重试
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-500">正在加载方案列表...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginated.map((solution) => {
                const id = String(solution.id ?? '');
                const title = readText(solution.title) || '无';
                const description = readText(solution.description) || '无';
                const category = readText(solution.category) || '无';
                const commercial = readText(solution.commercial) || '无';
                const opensource = readText(solution.opensource) || '无';
                const saving = readText(solution.costSaving) || '无';
                const commercialCost = readText(solution.costCompare?.commercial) || '无';
                const openCost = readText(solution.costCompare?.opensource) || '无';
                const stars = clampStars(solution.stars);
                const image = readText(solution.image);
                const tags = (solution.tags ?? []).map((tag) => readText(tag)).filter(Boolean);

                return (
                  <div
                    key={id}
                    className="group bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-[#0ea5e9]/30 transition-all duration-300 relative"
                  >
                    {solution.pinned && (
                      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-center">
                        <span className="bg-gradient-to-r from-[#f97316] to-[#ef4444] text-white text-xs font-bold px-4 py-1 rounded-b-xl flex items-center gap-1 shadow-sm">
                          <i className="ri-fire-fill"></i>热门推荐
                        </span>
                      </div>
                    )}
                    <div className="relative h-44 overflow-hidden">
                      {image ? (
                        <img
                          src={image}
                          alt={title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-slate-300 to-slate-500 flex items-center justify-center text-white/90 text-xs">
                          暂无封面
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                      <div className="absolute top-3 left-3 flex gap-2">
                        <span className="px-2 py-1 bg-[#0ea5e9] text-white text-xs font-medium rounded-md">{getCategoryLabel(category)}</span>
                      </div>
                      <div className="absolute top-3 right-3">
                        <span className="px-2 py-1 bg-[#f97316] text-white text-xs font-medium rounded-md">省 {saving}</span>
                      </div>
                    </div>

                    <div className="p-5">
                      <h3 className="font-bold text-gray-900 text-base mb-1">{title}</h3>
                      <p className="text-gray-500 text-sm mb-3 line-clamp-2">{description}</p>

                      <div className="bg-[#f8fafc] rounded-xl p-3 mb-4">
                        <div className="flex items-center justify-between text-xs">
                          <div>
                            <span className="text-gray-400">商业方案</span>
                            <div className="font-semibold text-gray-700 mt-0.5">{commercial}</div>
                            <div className="text-red-400 mt-0.5">{commercialCost}</div>
                          </div>
                          <div className="w-8 h-8 flex items-center justify-center">
                            <i className="ri-arrow-right-line text-[#0ea5e9] text-lg"></i>
                          </div>
                          <div className="text-right">
                            <span className="text-gray-400">平替方案</span>
                            <div className="font-semibold text-[#0ea5e9] mt-0.5">{opensource}</div>
                            <div className="text-[#10b981] mt-0.5">{openCost}</div>
                          </div>
                        </div>
                      </div>

                      {tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-4">
                          {tags.map((tag) => (
                            <button
                              key={tag}
                              onClick={() => { toggleTag(tag); setShowAdvanced(true); }}
                              className={`px-2 py-0.5 text-xs rounded-md transition-colors cursor-pointer ${
                                selectedTags.includes(tag)
                                  ? 'bg-[#0ea5e9] text-white'
                                  : 'bg-gray-50 text-gray-500 hover:bg-[#0ea5e9]/10 hover:text-[#0ea5e9]'
                              }`}
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        {stars > 0 ? (
                          <div className="flex gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <i key={i} className={`ri-star-fill text-sm ${i < stars ? 'text-[#f97316]' : 'text-gray-200'}`}></i>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">无评分</span>
                        )}
                        <Link
                          to={`/solutions/${id}`}
                          className="px-4 py-1.5 bg-[#0ea5e9] text-white text-sm font-medium rounded-lg hover:bg-[#0284c7] transition-all cursor-pointer whitespace-nowrap"
                        >
                          查看详情
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-20 text-gray-400">
                <i className="ri-search-line text-4xl mb-3 block"></i>
                <p className="mb-2">没有找到匹配的方案</p>
                {hasAdvancedFilter && (
                  <button onClick={resetAdvanced} className="text-sm text-[#0ea5e9] hover:underline cursor-pointer">清除高级筛选条件</button>
                )}
              </div>
            )}
          </>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 cursor-pointer transition-all"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: totalPages }).map((_, i) => {
              const p = i + 1;
              if (totalPages > 7 && Math.abs(p - currentPage) > 2 && p !== 1 && p !== totalPages) {
                if (p === currentPage - 3 || p === currentPage + 3) return <span key={p} className="text-gray-300 px-1">...</span>;
                return null;
              }
              return (
                <button
                  key={p}
                  onClick={() => handlePageChange(p)}
                  className={`w-9 h-9 flex items-center justify-center rounded-xl text-sm font-medium cursor-pointer transition-all ${
                    currentPage === p
                      ? 'bg-[#0ea5e9] text-white'
                      : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >{p}</button>
              );
            })}
            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 cursor-pointer transition-all"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
